import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Shield, CheckCircle, XCircle, Clock, AlertTriangle, FileText, Globe, Users, TrendingUp } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;
const ratingColors = { AAA: "text-[#22C55E]", AA: "text-[#22C55E]", "A": "text-[#22C55E]", "A-": "text-[#22C55E]", "B+": "text-yellow-400", B: "text-yellow-400", "B-": "text-orange-400", C: "text-red-400", NR: "text-[#A1B2A8]/40" };
const riskColors = { Low: "text-[#22C55E] bg-[#22C55E]/10 border-[#22C55E]/30", Moderate: "text-yellow-400 bg-yellow-400/10 border-yellow-400/30", High: "text-red-400 bg-red-400/10 border-red-400/30" };
const kycColors = { complete: "text-[#22C55E] bg-[#22C55E]/10 border-[#22C55E]/30", partial: "text-yellow-400 bg-yellow-400/10 border-yellow-400/30", pending: "text-orange-400 bg-orange-400/10 border-orange-400/30", incomplete: "text-red-400 bg-red-400/10 border-red-400/30", rejected: "text-red-400 bg-red-400/10 border-red-400/30" };
const docStatusColors = { verified: "text-[#22C55E]", pending: "text-yellow-400", rejected: "text-red-400", expired: "text-orange-400" };
const docStatusIcons = { verified: CheckCircle, pending: Clock, rejected: XCircle, expired: AlertTriangle };

export default function AdminProfiles() {
  const { user } = useAuth();
  const isDemo = user?.is_demo === true;
  const [profiles, setProfiles] = useState([]);
  const [docStats, setDocStats] = useState({});
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState("all");

  const fetchData = useCallback(async () => {
    try {
      const [p, d] = await Promise.all([
        axios.get(`${API}/admin/profiles`, { withCredentials: true }),
        axios.get(`${API}/admin/document-stats`, { withCredentials: true }),
      ]);
      setProfiles(p.data); setDocStats(d.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const viewDetail = async (id) => {
    try {
      const { data } = await axios.get(`${API}/admin/profiles/${id}`, { withCredentials: true });
      setDetail(data); setSelected(id);
    } catch (e) { console.error(e); }
  };

  const verifyDoc = async (docId, status) => {
    await axios.put(`${API}/admin/documents/${docId}/verify`, { status }, { withCredentials: true });
    if (selected) viewDetail(selected);
    fetchData();
  };

  const filtered = filter === "all" ? profiles : profiles.filter(p => p.entity_type === filter);

  return (
    <div data-testid="admin-profiles">
      {/* Stats */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-bold text-white">Profiles & KYC</h1>
          <p className="text-[#A1B2A8]/60 text-sm">Commercial profiles, multi-source corporate intelligence, document verification</p>
        </div>
        <div className="flex gap-2">
          {[["all", "All"], ["exporter", "Exporters"], ["buyer", "Buyers"]].map(([v, l]) => (
            <button key={v} onClick={() => { setFilter(v); setSelected(null); setDetail(null); }}
              className={`text-xs px-3 py-1.5 border transition-colors ${filter === v ? "text-[#22C55E] border-[#22C55E]/50 bg-[#22C55E]/10" : "text-[#A1B2A8] border-[#1A2E22] hover:text-white"}`}
              data-testid={`filter-${v}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        <div className="bg-[#09120D] border border-[#1A2E22] p-4"><p className="text-[#A1B2A8] text-xs">Profiles</p><p className="font-['Outfit'] text-xl font-bold text-white">{profiles.length}</p></div>
        <div className="bg-[#09120D] border border-[#1A2E22] p-4"><p className="text-[#A1B2A8] text-xs">Documents</p><p className="font-['Outfit'] text-xl font-bold text-white">{docStats.total || 0}</p></div>
        <div className="bg-[#09120D] border border-[#1A2E22] p-4"><p className="text-[#A1B2A8] text-xs">Verified</p><p className="font-['Outfit'] text-xl font-bold text-[#22C55E]">{docStats.verified || 0}</p></div>
        <div className="bg-[#09120D] border border-[#1A2E22] p-4"><p className="text-[#A1B2A8] text-xs">Pending</p><p className="font-['Outfit'] text-xl font-bold text-yellow-400">{docStats.pending || 0}</p></div>
        <div className="bg-[#09120D] border border-[#1A2E22] p-4"><p className="text-[#A1B2A8] text-xs">Rejected</p><p className="font-['Outfit'] text-xl font-bold text-red-400">{docStats.rejected || 0}</p></div>
        <div className="bg-[#09120D] border border-[#1A2E22] p-4"><p className="text-[#A1B2A8] text-xs">KYC Complete</p><p className="font-['Outfit'] text-xl font-bold text-[#22C55E]">{docStats.complete_kyc || 0}</p></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Profile List */}
        <div className="lg:col-span-1 space-y-2">
          {filtered.map((p) => (
            <button key={p.id} onClick={() => viewDetail(p.id)}
              className={`w-full text-left bg-[#09120D] border p-4 transition-colors ${selected === p.id ? "border-[#22C55E]/50" : "border-[#1A2E22] hover:border-[#22C55E]/30"}`}
              data-testid={`profile-${p.id}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-white text-sm font-medium truncate">{p.entity_name}</span>
                <span className={`font-['Outfit'] font-bold text-lg ${ratingColors[p.credit_rating] || "text-[#A1B2A8]"}`}>{p.credit_rating}</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] px-1.5 py-0.5 border capitalize ${p.entity_type === "exporter" ? "text-purple-400 border-purple-400/30 bg-purple-400/10" : "text-blue-400 border-blue-400/30 bg-blue-400/10"}`}>{p.entity_type}</span>
                <span className={`text-[10px] px-1.5 py-0.5 border ${kycColors[p.kyc_status] || ""}`}>KYC: {p.kyc_status}</span>
                <span className="text-[#A1B2A8]/40 text-[10px]">{p.country}</span>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Shield className={`w-3 h-3 ${p.confidence_score >= 80 ? "text-[#22C55E]" : p.confidence_score >= 50 ? "text-yellow-400" : "text-red-400"}`} />
                <span className="text-[#A1B2A8] text-xs">Confidence: {p.confidence_score}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Profile Detail */}
        <div className="lg:col-span-2">
          {!detail ? (
            <div className="bg-[#09120D] border border-[#1A2E22] p-12 text-center">
              <Users className="w-8 h-8 text-[#A1B2A8]/20 mx-auto mb-3" />
              <p className="text-[#A1B2A8]/40 text-sm">Select a profile to view details</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="profile-detail">
              {/* Profile Header */}
              <div className={`border p-6 ${detail.kyc_status === "rejected" ? "bg-red-400/5 border-red-400/20" : "bg-[#09120D] border-[#1A2E22]"}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-white font-['Outfit'] text-lg font-bold">{detail.entity_name}</p>
                    <p className="text-[#A1B2A8]/60 text-xs">{detail.legal_name} &middot; {detail.country} &middot; Est. {detail.year_established}</p>
                    <p className="text-[#A1B2A8]/40 text-xs mt-1 font-mono">
                      {detail.registration_label || "Registry ID"}: {detail.registration_number || detail.registration_id || detail.duns_number}
                    </p>
                    {detail.data_source && (
                      <p className="text-[#22C55E]/70 text-[10px] mt-1 flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        Multi-source corporate intelligence
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className={`font-['Outfit'] text-3xl font-bold ${ratingColors[detail.credit_rating] || "text-[#A1B2A8]"}`}>{detail.credit_rating}</p>
                    <span className={`text-xs px-2 py-0.5 border ${riskColors[detail.risk_indicator] || ""}`}>{detail.risk_indicator} Risk</span>
                  </div>
                </div>
                <p className="text-[#A1B2A8] text-sm leading-relaxed mt-3">{detail.summary}</p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {(detail.tags || []).map((t, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 bg-[#050A07] border border-[#1A2E22] text-[#A1B2A8]">{t}</span>
                  ))}
                </div>
              </div>

              {/* Commercial Metrics */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  ["Financial Strength", `${detail.financial_strength}/10`, detail.financial_strength >= 7 ? "text-[#22C55E]" : detail.financial_strength >= 4 ? "text-yellow-400" : "text-red-400"],
                  ["Revenue", detail.annual_revenue, "text-white"],
                  ["Employees", detail.employee_count?.toLocaleString(), "text-white"],
                  ["Trade Refs", detail.trade_references, "text-white"],
                ].map(([label, val, color]) => (
                  <div key={label} className="bg-[#050A07] border border-[#1A2E22] p-3">
                    <p className="text-[#A1B2A8] text-[10px] uppercase tracking-wider">{label}</p>
                    <p className={`font-['Outfit'] font-bold text-lg ${color}`}>{val}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  ["Payment Behavior", detail.payment_behavior, detail.payment_behavior === "Prompt" ? "text-[#22C55E]" : detail.payment_behavior === "Delinquent" ? "text-red-400" : "text-yellow-400"],
                  ["Litigation", detail.litigation_history, detail.litigation_history === "None" ? "text-[#22C55E]" : "text-red-400"],
                  ["KYC Status", detail.kyc_status, kycColors[detail.kyc_status]?.split(" ")[0] || "text-white"],
                ].map(([label, val, color]) => (
                  <div key={label} className="bg-[#050A07] border border-[#1A2E22] p-3">
                    <p className="text-[#A1B2A8] text-[10px] uppercase tracking-wider">{label}</p>
                    <p className={`font-semibold text-sm capitalize ${color}`}>{val}</p>
                  </div>
                ))}
              </div>

              {/* Documents */}
              {detail.documents && detail.documents.length > 0 && (
                <div className="bg-[#09120D] border border-[#1A2E22] p-5">
                  <h3 className="font-['Outfit'] text-sm font-semibold text-white mb-4 uppercase tracking-wider">Documents ({detail.documents.length})</h3>
                  <div className="space-y-2">
                    {detail.documents.map((doc) => {
                      const Icon = docStatusIcons[doc.status] || Clock;
                      return (
                        <div key={doc.id} className="flex items-center justify-between bg-[#050A07] border border-[#1A2E22] px-4 py-3" data-testid={`doc-${doc.id}`}>
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <Icon className={`w-4 h-4 flex-shrink-0 ${docStatusColors[doc.status]}`} />
                            <div className="min-w-0">
                              <p className="text-white text-sm truncate">{doc.doc_name}</p>
                              <p className="text-[#A1B2A8]/40 text-xs font-mono">{doc.doc_reference}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                            <span className={`text-xs capitalize ${docStatusColors[doc.status]}`}>{doc.status}</span>
                            {doc.status === "pending" && !isDemo && (
                              <div className="flex gap-1">
                                <button onClick={() => verifyDoc(doc.id, "verified")} className="text-[#22C55E] text-[10px] border border-[#22C55E]/30 px-2 py-0.5 hover:bg-[#22C55E]/10" data-testid={`verify-${doc.id}`}>Verify</button>
                                <button onClick={() => verifyDoc(doc.id, "rejected")} className="text-red-400 text-[10px] border border-red-400/30 px-2 py-0.5 hover:bg-red-400/10" data-testid={`reject-${doc.id}`}>Reject</button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
